/**
 * Polyfills et détections de fonctionnalités pour la compatibilité multi-navigateurs
 * Compatible avec: Chrome 90+, Firefox 90+, Safari 14+, Edge 90+, iOS Safari 14+
 */

// Polyfill pour IntersectionObserver (Safari < 12.1, IE11)
export const supportsIntersectionObserver = typeof IntersectionObserver !== 'undefined';

// Polyfill pour ResizeObserver (Safari < 13.1)
export const supportsResizeObserver = typeof ResizeObserver !== 'undefined';

// Polyfill pour smooth scroll (Safari < 15.4)
export const supportsSmoothScroll = 'scrollBehavior' in document.documentElement.style;

// Polyfill pour requestIdleCallback (Safari)
export const supportsRequestIdleCallback = typeof requestIdleCallback !== 'undefined';

// Fallback pour requestIdleCallback
if (!supportsRequestIdleCallback) {
  (window as unknown as { requestIdleCallback: typeof requestIdleCallback }).requestIdleCallback = (
    callback: IdleRequestCallback
  ): number => {
    const start = Date.now();
    return window.setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, 1) as unknown as number;
  };

  (window as unknown as { cancelIdleCallback: typeof cancelIdleCallback }).cancelIdleCallback = (
    id: number
  ): void => {
    clearTimeout(id);
  };
}

/**
 * Smooth scroll fallback pour Safari
 * Utilise scrollTo avec behavior: smooth si supporté, sinon animation JS
 */
export const smoothScrollTo = (element: HTMLElement, options?: ScrollToOptions): void => {
  if (supportsSmoothScroll) {
    element.scrollTo(options);
  } else {
    // Fallback avec animation JS pour Safari < 15.4
    const targetY = options?.top ?? 0;
    const startY = element.scrollTop;
    const distance = targetY - startY;
    const duration = 300;
    let startTime: number | null = null;

    const step = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function (easeOutCubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      element.scrollTop = startY + distance * easeProgress;

      if (timeElapsed < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }
};

/**
 * Smooth scroll vers un élément spécifique
 */
export const scrollIntoViewPolyfill = (
  element: HTMLElement, 
  options?: ScrollIntoViewOptions
): void => {
  if (supportsSmoothScroll && element.scrollIntoView) {
    element.scrollIntoView(options);
  } else {
    // Fallback pour Safari < 15.4
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - (options?.block === 'center' ? window.innerHeight / 2 : 0);
    
    const startY = window.pageYOffset;
    const distance = targetY - startY;
    const duration = 300;
    let startTime: number | null = null;

    const step = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      window.scrollTo(0, startY + distance * easeProgress);

      if (timeElapsed < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }
};

/**
 * Détection des fonctionnalités CSS
 */
export const cssSupports = {
  backdropFilter: CSS.supports?.('backdrop-filter', 'blur(1px)') ?? false,
  webkitBackdropFilter: CSS.supports?.('-webkit-backdrop-filter', 'blur(1px)') ?? false,
  grid: CSS.supports?.('display', 'grid') ?? true,
  flexGap: CSS.supports?.('gap', '1px') ?? true,
  containerQueries: CSS.supports?.('container-type', 'inline-size') ?? false,
};

/**
 * Détection du type d'appareil et navigateur
 */
export const browserInfo = {
  isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
  isChrome: /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent),
  isFirefox: /Firefox/.test(navigator.userAgent),
  isEdge: /Edg/.test(navigator.userAgent),
  isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
};

/**
 * Préférence pour les animations réduites
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
};

/**
 * Initialisation des polyfills au démarrage
 */
export const initPolyfills = (): void => {
  // Ajouter des classes CSS pour le feature detection
  const html = document.documentElement;
  
  if (!supportsSmoothScroll) {
    html.classList.add('no-smooth-scroll');
  }
  
  if (!cssSupports.backdropFilter && !cssSupports.webkitBackdropFilter) {
    html.classList.add('no-backdrop-filter');
  }
  
  if (browserInfo.isSafari) {
    html.classList.add('is-safari');
  }
  
  if (browserInfo.isIOS) {
    html.classList.add('is-ios');
  }
  
  if (prefersReducedMotion()) {
    html.classList.add('reduce-motion');
  }

  // Log de diagnostic en développement
  if (import.meta.env.DEV) {
    console.log('[Polyfills] Browser compatibility:', {
      smoothScroll: supportsSmoothScroll,
      intersectionObserver: supportsIntersectionObserver,
      resizeObserver: supportsResizeObserver,
      requestIdleCallback: supportsRequestIdleCallback,
      backdropFilter: cssSupports.backdropFilter || cssSupports.webkitBackdropFilter,
      browser: browserInfo,
    });
  }
};
